import { Button, Box, Checkbox, CircularProgress, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, OutlinedInput, TextField, Typography, MenuItem, FormControl, InputLabel, Stack, Alert } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import { validateEmail, validatePassword, validatePostCode } from "../../helpers/validation";
import cssVars from '../../styles/vars.module.scss'
import { useMutation, useQuery } from "@apollo/client";
import { useRouter } from "next/router";
import { CREATE_CUSTOMER, CREATE_CUSTOMER_FILES } from "../../graphql/customer/mutations";
import { CustomerContext } from "../../Providers/CustomerContext";
import { LOGIN } from "../../graphql/authentication/mutations";
import PrivacyFiled from "../privacyFiled/privacyFiled";
import FormControlSelect from "../formControlSelect/formControlSelect";
import { useAuthStore } from "../../stores/authStore";
import useTranslation from "../../hooks/useTranslation";
import { CREATE_CART, MERGE_CARTS } from "../../graphql/cart/mutations";
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';

const RegisterForm = ({ countries, stores, selectedForm }) => {
    const { t } = useTranslation()
    const router = useRouter()
    const [showPassword, setShowPassword] = useState(false)
    const [formCheck, setFormCheck] = useState(false)
    const isAuthenticated = useAuthStore((state) => state.token).length > 0
    const signIn = useAuthStore((state) => state.signIn)
    const [agree, setAgree] = useState(false)
    const [createdCustomer, setCreatedCustomer] = useState(false)

    const [userData, setUserData] = useState({
        language: "",
        firstname: "",
        lastname: "",
        telephone: "",
        email: "",
        password: "",
        gender: "",
        company: "",
        indicative: "",
    })

    const { customerData, createCustomerAddress, subscribeNewsLetter, createAddressLoading, logOut } = useContext(CustomerContext)
    const [createAccount, { data, error }] = useMutation(CREATE_CUSTOMER)
    const [login] = useMutation(LOGIN)

    const [fullyRegistered, setFullyRegistered] = useState(false)

    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (countries && countries.countries.length === 1) {
            setUserData({ ...userData, country_code: countries.countries[0].id })
        }
    }, [countries])

    useEffect(() => {
        if (fullyRegistered) {
            if (selectedForm === "2") {
                logOut('/request-submitted-message.html')
            }
            else {
                router.push('/', null, { locale: customerData.store_code })
            }
        }
    }, [fullyRegistered])

    const [scrollToError, setScrollToError] = useState(true)


    const cartId = useAuthStore((state) => state.cartId)
    const setCartId = useAuthStore((state) => state.setCartId)
    const setLoginDone = useAuthStore((state) => state.setLoginDone)

    const [createCart] = useMutation(CREATE_CART)
    const [mergeCarts] = useMutation(MERGE_CARTS)

    useEffect(() => {
        if (isAuthenticated) {
            mergeCarts({
                variables: {
                    source_cart_id: cartId,
                },
                onCompleted: (data) => {
                    if (data?.mergeCarts?.id) {
                        setCartId(data.mergeCarts.id)
                        setLoginDone(true)
                        setCreatedCustomer(true)
                        setFullyRegistered(true)
                    }
                },
                onError: () => {
                    createCart({
                        onCompleted: (data) => {
                            setCartId(data.cart_id)
                            setLoginDone(true)
                            setLoading(false)
                            setFullyRegistered(true)
                        },
                        onError: (err) => {
                            enqueueSnackbar(err.message)
                            signOut()
                            setLoading(false)
                        }
                    })
                }
            })
        }
    }, [isAuthenticated])

    const handleSubmit = (e) => {
        e.preventDefault()
        setFormCheck(true)
        
        if (selectedForm === "1") {
            delete userData.company
        }
        if (e.target.checkValidity() &&
            validateEmail(userData.email) &&
            validatePassword(userData.password) &&
            userData.language && userData.firstname && userData.lastname && userData.indicative && userData.telephone &&
            agree && userData.gender && (selectedForm === "2" ? userData.company : true)
        ) {            
            setLoading(true)
            createAccount({
                variables: {
                    email: userData.email,
                    password: userData.password,
                    firstname: userData.firstname,
                    lastname: userData.lastname,
                    telephone: userData.indicative + "-" + userData.telephone,
                    gender: userData.gender,
                    company: userData.company,
                },
                context: {
                    headers: {
                        store: userData.language
                    }
                },
                onCompleted: () => {
                    login({
                        variables: {
                            email: userData.email,
                            password: userData.password
                        },
                        onCompleted: (data) => {
                            localStorage.setItem('customer', { firstname: '', lastname: '', email: '' })
                            signIn(data?.login.token)
                            setCreatedCustomer(true)
                        },
                        onError: () => { setLoading(false) }
                    })
                },
                onError: () => { setLoading(false); }
            })
        }
        else {
            setScrollToError(true)
        }
    }

    useEffect(() => {
        if (scrollToError && formCheck && document.querySelector("div.register-error-msg")) {
            document.querySelector("div.register-error-msg").parentNode.scrollIntoView({ behavior: 'smooth' })
            setScrollToError(false)
        }
    }, [scrollToError, formCheck])

    return (
        <Box component="form" onSubmit={handleSubmit} autoComplete="off" sx={{ mt: 3 }}>
            <Grid container item columns={12} rowSpacing="22px" columnSpacing={2} >
                {error ?
                    <Grid item xs={12}>
                        <Stack sx={{ width: '100%' }}>
                            <Alert severity="error">{error.message}</Alert>
                        </Stack>
                    </Grid>
                    :
                    null
                }
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", py: 2 }}>
                    <PersonOutlinedIcon sx={{ mb: 1, borderRadius: "64px", fontSize: "52px", color: cssVars.lightBlack, backgroundColor: cssVars.lightGray, fontWeight: "thin", p: 1.5 }} />
                    {t("Create account")}
                </Box>
                <Grid item xs={12} order={1} position="relative">
                    <TextField
                        label={t("Email") + " *"}
                        fullWidth
                        className="custom-input"
                        type="email"
                        InputLabelProps={{
                            style: {
                                color: `${cssVars.extraDarkGray} ! important`
                            }
                        }}
                        error={formCheck && !validateEmail(userData.email)}
                        onChange={(e) => { setUserData({ ...userData, email: e.target.value }) }}
                    />
                    <Grid className={formCheck && !validateEmail(userData.email) ? "register-error-msg" : ""} display={formCheck && !validateEmail(userData.email) ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("email is required")}</Grid>
                </Grid>
                <Grid item xs={12} order={2} position="relative">
                    <FormControl
                        className="custom-input"
                        fullWidth
                        variant="outlined"
                        error={formCheck && !validatePassword(userData.password)}
                        onChange={(e) => { setUserData({ ...userData, password: e.target.value }) }}
                    >
                        <InputLabel htmlFor="outlined-adornment-password" sx={{ color: `${cssVars.red} ! important` }}>{t("Password") + " *"}</InputLabel>
                        <OutlinedInput
                            type={showPassword ? 'text' : 'password'}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        edge="end"
                                        onClick={() => { setShowPassword(!showPassword) }}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label={t("Password")}
                        />
                    </FormControl>
                    <Grid className={formCheck && !validatePassword(userData.password) ? "register-error-msg" : ""} display={formCheck && !validatePassword(userData.password) ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >
                        {!userData.password && t("Password is required")}
                        {!validatePassword(userData.password) && userData.password && t("Password must have at least 8 chars")}
                    </Grid>
                </Grid>
                {
                    {
                        "2": <Grid item xs={12} order={3} position="relative">
                            <TextField
                                label={t("company") + " *"}
                                fullWidth
                                className="custom-input"
                                type="text"
                                InputLabelProps={{
                                    style: {
                                        color: `${cssVars.extraDarkGray} ! important`
                                    }
                                }}
                                error={formCheck && !userData.company}
                                onChange={(e) => { setUserData({ ...userData, company: e.target.value }) }}
                            />
                            <Grid className={formCheck && !userData.company ? "register-error-msg" : ""} display={formCheck && !userData.company ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("company name is required")}</Grid>
                        </Grid>,
                    }[selectedForm]
                }

                <Grid item xs={12} lg={6} order={selectedForm === "1" ? 3 : 4} position="relative" >
                    <FormControlSelect className="custom-input" value={userData.gender} error={formCheck && !userData.gender} label={t("Civility") + " *"} onChange={(e) => { setUserData({ ...userData, gender: e.target.value }) }} >
                        <MenuItem value={1}>{t("Sir")}</MenuItem>
                        <MenuItem value={2}>{t("Madams")}</MenuItem>
                    </FormControlSelect >
                    <Grid className={formCheck && !userData.company ? "register-error-msg" : ""} display={formCheck && !userData.gender ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("Civility is required")}</Grid>
                </Grid>
                <Grid item xs={12} lg={6} order={selectedForm === "1" ? 4 : 5} position="relative" >
                    <TextField
                        label={t("First Name") + " *"}
                        fullWidth
                        className="custom-input"
                        type="text"
                        InputLabelProps={{
                            style: {
                                color: `${cssVars.extraDarkGray} ! important`
                            }
                        }}
                        error={formCheck && !userData.firstname}
                        onChange={(e) => { setUserData({ ...userData, firstname: e.target.value }) }}
                    />
                    <Grid className={formCheck && !userData.firstname ? "register-error-msg" : ""} display={formCheck && !userData.firstname ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("first name is required")}</Grid>
                </Grid>

                <Grid item xs={12} order={selectedForm === "1" ? 5 : 6} position="relative">
                    <TextField
                        label={t("Last Name") + " *"}
                        fullWidth
                        className="custom-input"
                        type="text"
                        InputLabelProps={{
                            style: {
                                color: `${cssVars.extraDarkGray} !important`
                            }
                        }}
                        error={formCheck && !userData.lastname}
                        onChange={(e) => { setUserData({ ...userData, lastname: e.target.value }) }}
                    />
                    <Grid className={formCheck && !userData.lastname ? "register-error-msg" : ""} display={formCheck && !userData.lastname ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("last name is required")}</Grid>
                </Grid>

                <Grid item xs={12} lg={4.5} order={selectedForm === "1" ? 6 : 7} position="relative" >
                    <FormControlSelect className="custom-input" value={userData.indicative} error={formCheck && !userData.indicative} label={t("Indicative") + " *"} onChange={(e) => { setUserData({ ...userData, indicative: e.target.value }) }} >
                        <MenuItem value="0041">0041</MenuItem>
                        <MenuItem value="0033">0033</MenuItem>
                        <MenuItem value="0049">0049</MenuItem>
                    </FormControlSelect >
                    <Grid className={formCheck && !userData.indicative ? "register-error-msg" : ""} display={formCheck && !userData.indicative ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("Indicative is required")}</Grid>
                </Grid>
                <Grid item xs={12} lg={7.5} order={selectedForm === "1" ? 7 : 8} position="relative" >
                    <TextField
                        label={t("Phone number") + " *"}
                        fullWidth
                        className="custom-input"
                        type="text"
                        InputLabelProps={{
                            style: {
                                color: `${cssVars.extraDarkGray} !important`
                            }
                        }}
                        error={formCheck && !userData.telephone}
                        onChange={(e) => { setUserData({ ...userData, telephone: e.target.value }) }}
                    />
                    <Grid className={formCheck && !userData.telephone ? "register-error-msg" : ""} display={formCheck && !userData.telephone ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("Phone is required")}</Grid>
                </Grid>

                <Grid item xs={12} order={selectedForm === "1" ? 8 : 9} position="relative" sx={{ mt: "8px" }} >
                    <FormControlSelect className="custom-input" value={userData.language} error={formCheck && !userData.language} label={t("Language of preference" + " *")} onChange={(e) => { setUserData({ ...userData, language: e.target.value }) }} >
                        {stores.availableStores.map((store) =>
                            <MenuItem key={store.id} value={store.store_code}>{store.store_name}</MenuItem>
                        )}
                    </FormControlSelect >
                    <Grid className={formCheck && !userData.language ? "register-error-msg" : ""} display={formCheck && !userData.language ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("language is required")}</Grid>
                </Grid>

                <Grid item xs={12} order={selectedForm === "1" ? 9 : 10} position="relative"
                    sx={{
                        "& .MuiFormControlLabel-root": {
                            alignItems: "start",
                        }
                    }}
                >
                    <PrivacyFiled formCheck={formCheck} checked={agree} onChange={(e) => { setAgree(e.target.checked) }} />
                    <Grid className={formCheck && !agree ? "register-error-msg" : ""} display={formCheck && !agree ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("Agreeing to terms and privacy is required.")}</Grid>
                </Grid>
                <Grid item xs={12} order={selectedForm === "1" ? 10 : 11} >
                    <Button
                        sx={{
                            height: "50px !important",
                            fontSize: "16px",
                            fontWeight: '400 !important',
                            mt: "30px !important",
                            borderRadius: "25px !important",
                            backgroundColor: `${!loading ? cssVars.primary : cssVars.darkGray} ! important`,
                            color: `${cssVars.white} ! important`,
                            border: `1px solid ${!loading ? cssVars.primary : cssVars.darkGray} ! important`,
                            ':hover': {
                                bgcolor: `${!loading ? cssVars.hoverColor : cssVars.darkGray} ! important `,
                                borderColor: `${!loading ? cssVars.hoverColor : cssVars.darkGray} ! important`,
                                color: `${cssVars.white} ! important`
                            },
                            textTransform: 'unset'
                        }}
                        fullWidth type="submit"
                        disabled={loading}
                    >
                        {t("Create account")}
                        {loading ? <CircularProgress sx={{ ml: 1 }} style={{ width: "15px", height: "15px", color: cssVars.white }} /> : null}
                    </Button>
                </Grid>
            </Grid >
        </Box>
    );
}

export default RegisterForm;